import type { ReactNode } from "react";
import { useCallback, useContext, useMemo, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

import {
  type INavigationGuard,
  type INavigationGuardContextValue,
  NavigationGuardContext,
} from "@/hooks/use-navigation-guard";

interface INavigationGuardProviderProps {
  children: ReactNode;
}

// NOTE: Wraps react-router's internal `navigator` with an interceptor that
// consults registered guards before every `push` / `replace`. Both
// `<Link>` / `<NavLink>` clicks and programmatic `useNavigate()` calls go
// through that navigator, so a single intercept covers both anchor and
// button-driven navigation — fixing the "breadcrumb home button bypasses
// the upload-leave dialog" gap that the click-capture-only approach left.
//
// `go(delta)` (browser back/forward) is intentionally NOT intercepted here
// — the upload guard's `popstate` handler already covers that path, and
// re-routing `go` through the dialog would double-trigger on confirm.
//
// We rely on `UNSAFE_NavigationContext` because react-router does not
// expose a stable hook for replacing the navigator. The `UNSAFE_` prefix
// signals API instability, not unsafety; libraries like
// `react-router-prompt` use the same approach. If react-router removes it
// in a future major, we'd switch to whatever official blocker API ships
// with that version (data routers already expose `useBlocker`). NSAI-4174:
// v7 still exports it, and `useNavigate()` / `<Link>` still call
// `navigator.push` / `replace`, so the intercept holds there unchanged.
export const NavigationGuardProvider = ({
  children,
}: INavigationGuardProviderProps): JSX.Element => {
  const original = useContext(UNSAFE_NavigationContext);
  const guards = useRef(new Map<string, INavigationGuard>());

  // NOTE: Tries each registered guard in registration order. Returns true if
  // any guard intercepted (caller should NOT proceed); the guard owns the
  // dialog and will call `proceed()` if the user confirms.
  const dispatchToGuard = useCallback((proceed: () => void): boolean => {
    for (const guard of guards.current.values()) {
      if (guard.shouldBlock()) {
        guard.onBlock(proceed);
        return true;
      }
    }
    return false;
  }, []);

  const wrappedNavigator = useMemo(() => {
    const originalNavigator = original.navigator;
    return {
      ...originalNavigator,
      push: (
        to: Parameters<typeof originalNavigator.push>[0],
        state?: Parameters<typeof originalNavigator.push>[1],
      ): void => {
        if (dispatchToGuard(() => originalNavigator.push(to, state))) return;
        originalNavigator.push(to, state);
      },
      replace: (
        to: Parameters<typeof originalNavigator.replace>[0],
        state?: Parameters<typeof originalNavigator.replace>[1],
      ): void => {
        if (dispatchToGuard(() => originalNavigator.replace(to, state))) {
          return;
        }
        originalNavigator.replace(to, state);
      },
    };
  }, [original.navigator, dispatchToGuard]);

  const wrappedContext = useMemo(
    () => ({ ...original, navigator: wrappedNavigator }),
    [original, wrappedNavigator],
  );

  const guardContextValue = useMemo<INavigationGuardContextValue>(
    () => ({
      registerGuard: (id, guard) => {
        guards.current.set(id, guard);
      },
      unregisterGuard: (id) => {
        guards.current.delete(id);
      },
    }),
    [],
  );

  return (
    <NavigationGuardContext.Provider value={guardContextValue}>
      <UNSAFE_NavigationContext.Provider value={wrappedContext}>
        {children}
      </UNSAFE_NavigationContext.Provider>
    </NavigationGuardContext.Provider>
  );
};
